import { renderSettings } from "@shared/RenderSettings.js";
import { CYODesign } from "@shared/CYODesign.js";
import { Product } from "@shared/Product.js";

export class OptionPanels {
    constructor() {
        this.originalDevice = null;
        this.parameterOverrides = {};
        this.parameterUpdateTimer = null;
        this.rerenderCallback = null;
    }

    setRerenderCallback(callback) {
        this.rerenderCallback = callback;
    }

    cloneDevice(device) {
        return JSON.parse(JSON.stringify(device));
    }

    getDeviceWithOverrides(device) {
        const deviceCopy = this.cloneDevice(device);
        
        Object.keys(this.parameterOverrides).forEach(paramName => {
            const override = this.parameterOverrides[paramName];
            if (override !== undefined && override !== null) {
                deviceCopy[paramName] = override;
            }
        });
        
        return deviceCopy;
    }

    resetParameterOverrides() {
        this.parameterOverrides = {};
        // Only update UI if we have an original device
        if (this.originalDevice) {
            this.updateParameterUI();
        }
    }

    updateParameterUI() {
        if (!this.originalDevice) return;
        
        const parameterNames = [
            "safeAreaInsets",
            "cameraCutoutFrame",
            "userImageMargins",
            "borderSpacingRatio",
            "stickerScaleFactor",
            "borderStickerMargin",
            "patternCnt",
            "patternSpacing",
            "patternInsets",
            "borderDrawStartOffsets",
            "sideStickerCnt"
        ];
        
        parameterNames.forEach(paramName => {
            const originalValue = this.originalDevice[paramName];
            let currentValue = this.parameterOverrides[paramName] !== undefined 
                ? this.parameterOverrides[paramName] 
                : originalValue;
            
            // If currentValue is null/undefined, fall back to RenderSettings defaults
            if (currentValue == null) {
                currentValue = renderSettings(this.originalDevice.productCategory, paramName);
            }
            
            // If still null, provide default values based on parameter type
            if (currentValue == null) {
                if (paramName === "patternCnt" || paramName === "patternSpacing" || paramName === "patternInsets") {
                    currentValue = [0, 0];
                } else if (paramName === "cameraCutoutFrame") {
                    currentValue = [0, 0, 0, 0];
                } else if (paramName === "safeAreaInsets" || paramName === "userImageMargins" ||
                           paramName === "borderDrawStartOffsets" || paramName === "sideStickerCnt") {
                    currentValue = [0, 0, 0, 0];
                } else if (paramName === "stickerScaleFactor") {
                    currentValue = 1.0;
                } else {
                    currentValue = 0;
                }
            }
                
            if (Array.isArray(currentValue)) {
                currentValue.forEach((value, index) => {
                    let suffix;
                    if (paramName === "patternCnt") {
                        suffix = ["rows", "cols"][index];
                    } else if (paramName === "patternSpacing") {
                        suffix = ["vertical", "horizontal"][index];
                    } else if (paramName === "patternInsets") {
                        suffix = ["column", "row"][index];
                    } else if (paramName === "cameraCutoutFrame") {
                        suffix = ["x", "y", "width", "height"][index];
                    } else {
                        suffix = ["top", "right", "bottom", "left"][index];
                    }
                    
                    const input = document.getElementById(`${paramName}-${suffix}`);
                    const slider = document.getElementById(`${paramName}-${suffix}-slider`);
                    
                    if (input && slider) {
                        input.value = value || 0;
                        slider.value = value || 0;
                        
                        this.updateParameterHighlight(paramName, index, value);
                    }
                });
            } else {
                const input = document.getElementById(paramName);
                const slider = document.getElementById(`${paramName}-slider`);
                
                if (input && slider) {
                    input.value = currentValue || 0;
                    slider.value = currentValue || 0;
                    
                    this.updateParameterHighlight(paramName, null, currentValue);
                }
            }
            
            this.updateRowHighlight(paramName);
        });
    }

    updateParameterHighlight(paramName, index, currentValue) {
        let originalValue;
        if (index !== null && index !== undefined) {
            if (this.originalDevice[paramName] && this.originalDevice[paramName][index] !== undefined) {
                originalValue = this.originalDevice[paramName][index];
            } else {
                const defaultValues = renderSettings(this.originalDevice.productCategory, paramName);
                originalValue = defaultValues ? defaultValues[index] : 0;
            }
            
            let suffix;
            if (paramName === "patternCnt") {
                suffix = ["rows", "cols"][index];
            } else if (paramName === "patternSpacing") {
                suffix = ["horizontal", "vertical"][index];
            } else if (paramName === "patternInsets") {
                suffix = ["column", "row"][index];
            } else if (paramName === "cameraCutoutFrame") {
                suffix = ["x", "y", "width", "height"][index];
            } else {
                suffix = ["top", "right", "bottom", "left"][index];
            }
            
            const input = document.getElementById(`${paramName}-${suffix}`);
            const slider = document.getElementById(`${paramName}-${suffix}-slider`);
            
            const isModified = Math.abs(currentValue - originalValue) > 0.001;
            if (input) {
                input.classList.toggle("parameter-modified", isModified);
            }
            if (slider) {
                slider.classList.toggle("parameter-modified", isModified);
            }
        } else {
            originalValue = this.originalDevice[paramName] !== undefined ? this.originalDevice[paramName] : renderSettings(this.originalDevice.productCategory, paramName);

            const input = document.getElementById(paramName);
            const slider = document.getElementById(`${paramName}-slider`);

            const defaultValue = (paramName === "stickerScaleFactor") ? 1.0 : 0;
            const isModified = Math.abs(currentValue - (originalValue ?? defaultValue)) > 0.001;
            if (input) {
                input.classList.toggle("parameter-modified", isModified);
            }
            if (slider) {
                slider.classList.toggle("parameter-modified", isModified);
            }
        }
    }

    updateRowHighlight(paramName) {
        const hasModifications = this.parameterOverrides[paramName] !== undefined;
        
        const parameterRows = document.querySelectorAll(`[id^="${paramName}-"]`);
        const parentRows = new Set();
        
        parameterRows.forEach(element => {
            const row = element.closest('.parameter-row');
            if (row) {
                parentRows.add(row);
            }
        });
        
        parentRows.forEach(row => {
            row.classList.toggle("has-modifications", hasModifications);
        });
    }

    debouncedParameterUpdate() {
        if (this.parameterUpdateTimer) {
            clearTimeout(this.parameterUpdateTimer);
        }
        
        this.parameterUpdateTimer = setTimeout(() => {
            if (this.rerenderCallback) {
                this.rerenderCallback();
            }
        }, 300);
    }

    togglePanel(panelId, toggleId, otherPanelId, otherToggleId) {
        const content = document.getElementById(panelId);
        const toggle = document.getElementById(toggleId);
        const otherContent = document.getElementById(otherPanelId);
        const otherToggle = document.getElementById(otherToggleId);
        
        if (!content.classList.contains("expanded")) {
            content.classList.add("expanded");
            toggle.textContent = "Collapse";
            
            otherContent.classList.remove("expanded");
            otherToggle.textContent = "Expand";
        } else {
            content.classList.remove("expanded");
            toggle.textContent = "Expand";
        }
    }

    resetParameter(paramName, index) {
        if (index !== null && index !== undefined) {
            if (!this.parameterOverrides[paramName]) {
                this.parameterOverrides[paramName] = [...(this.originalDevice[paramName] || [])];
            }
            
            let resetValue;
            if (this.originalDevice[paramName] && this.originalDevice[paramName][index] !== undefined) {
                resetValue = this.originalDevice[paramName][index];
                console.log(`Reset ${paramName}[${index}] to original device value: ${resetValue}`);
            } else {
                const defaultValues = renderSettings(this.originalDevice.productCategory, paramName);
                resetValue = defaultValues ? defaultValues[index] : 0;
                console.log(`Reset ${paramName}[${index}] to RenderSettings default: ${resetValue} (product: ${this.originalDevice.productCategory})`);
            }
            
            this.parameterOverrides[paramName][index] = resetValue;
        } else {
            delete this.parameterOverrides[paramName];
        }
        
        this.updateParameterUI();
        this.debouncedParameterUpdate();
    }

    resetAllParameters() {
        this.resetParameterOverrides();
        this.debouncedParameterUpdate();
    }

    resetDebugOptions() {
        // Update CYODesign.current directly
        CYODesign.current.debugOptions.drawBgs = false;
        CYODesign.current.debugOptions.disableRotations = false;
        CYODesign.current.debugOptions.showSafeArea = false;
        CYODesign.current.debugOptions.showBleedInsets = false;
        CYODesign.current.debugOptions.hideProduct = false;
        CYODesign.current.debugOptions.hideMask = false;
        CYODesign.current.debugOptions.isolateMask = false;
        CYODesign.current.debugOptions.hideOverlay = false;
        CYODesign.current.debugOptions.hideColoredOverlay = false;

        // Update checkboxes without triggering events
        const debugCheckboxes = [
            "debug-bgs-checkbox",
            "disable-rotations-checkbox", 
            "show-safe-area-checkbox",
            "show-bleed-insets-checkbox",
            "hide-product-checkbox",
            "hide-mask-checkbox",
            "isolate-mask-checkbox",
            "hide-overlay-checkbox",
            "hide-colored-overlay-checkbox"
        ];

        // Note: show-print-image-checkbox is not included as it's not a debug option

        debugCheckboxes.forEach(checkboxId => {
            const checkbox = document.getElementById(checkboxId);
            if (checkbox && !checkbox.disabled) {
                checkbox.checked = false;
            }
        });

        // Save settings and trigger single re-render
        if (window.saveSettings) {
            window.saveSettings();
        }
        
        if (this.rerenderCallback) {
            this.rerenderCallback();
        }
    }

    updateParameterValue(paramName, index, value) {
        if (index !== null && index !== undefined) {
            if (!this.parameterOverrides[paramName]) {
                let fallbackArray;
                if (this.originalDevice[paramName]) {
                    fallbackArray = this.originalDevice[paramName];
                } else {
                    fallbackArray = renderSettings(this.originalDevice.productCategory, paramName) || 
                        ((paramName === "patternCnt" || paramName === "patternSpacing" || paramName === "patternInsets") ? [0, 0] : [0, 0, 0, 0]);
                }
                this.parameterOverrides[paramName] = [...fallbackArray];
            }
            this.parameterOverrides[paramName][index] = value;
            this.updateParameterHighlight(paramName, index, value);
        } else {
            this.parameterOverrides[paramName] = value;
            this.updateParameterHighlight(paramName, null, value);
        }
        
        this.updateRowHighlight(paramName);
        this.debouncedParameterUpdate();
    }

    setupParameterEventListeners() {
        document.getElementById("parameter-panel-toggle").addEventListener("click", () => {
            this.togglePanel(
                "parameter-panel-content", 
                "parameter-panel-toggle",
                "drawing-options-content", 
                "drawing-options-toggle"
            );
        });

        document.getElementById("drawing-options-toggle").addEventListener("click", () => {
            this.togglePanel(
                "drawing-options-content", 
                "drawing-options-toggle",
                "parameter-panel-content", 
                "parameter-panel-toggle"
            );
        });

        document.getElementById("reset-all-parameters").addEventListener("click", () => {
            this.resetAllParameters();
        });

        document.getElementById("reset-debug-options").addEventListener("click", () => {
            this.resetDebugOptions();
        });

        const parameterConfigs = [
            { name: "safeAreaInsets", isArray: true, suffixes: ["top", "right", "bottom", "left"] },
            { name: "cameraCutoutFrame", isArray: true, suffixes: ["x", "y", "width", "height"] },
            { name: "userImageMargins", isArray: true, suffixes: ["top", "right", "bottom", "left"] },
            { name: "borderSpacingRatio", isArray: false },
            { name: "stickerScaleFactor", isArray: false },
            { name: "borderStickerMargin", isArray: true, suffixes: ["top", "right", "bottom", "left"] },
            { name: "patternCnt", isArray: true, suffixes: ["rows", "cols"] },
            { name: "patternSpacing", isArray: true, suffixes: ["vertical", "horizontal"] },
            { name: "patternInsets", isArray: true, suffixes: ["column", "row"] },
            { name: "borderDrawStartOffsets", isArray: true, suffixes: ["top", "right", "bottom", "left"] },
            { name: "sideStickerCnt", isArray: true, suffixes: ["top", "right", "bottom", "left"] }
        ];

        parameterConfigs.forEach(config => {
            if (config.isArray) {
                config.suffixes.forEach((suffix, index) => {
                    const input = document.getElementById(`${config.name}-${suffix}`);
                    const slider = document.getElementById(`${config.name}-${suffix}-slider`);
                    
                    if (input && slider) {
                        input.addEventListener("input", (e) => {
                            const value = parseFloat(e.target.value);
                            slider.value = value;
                            this.updateParameterValue(config.name, index, value);
                        });
                        
                        slider.addEventListener("input", (e) => {
                            const value = parseFloat(e.target.value);
                            input.value = value;
                            this.updateParameterValue(config.name, index, value);
                        });
                    }
                });
            } else {
                const input = document.getElementById(config.name);
                const slider = document.getElementById(`${config.name}-slider`);
                
                if (input && slider) {
                    input.addEventListener("input", (e) => {
                        const value = parseFloat(e.target.value);
                        slider.value = value;
                        this.updateParameterValue(config.name, null, value);
                    });
                    
                    slider.addEventListener("input", (e) => {
                        const value = parseFloat(e.target.value);
                        input.value = value;
                        this.updateParameterValue(config.name, null, value);
                    });
                }
            }
        });
    }

    setOriginalDevice(device) {
        this.originalDevice = device;
        this.updateUIVisibility();
    }

    updateUIVisibility() {
        if (!this.originalDevice) return;

        const isGrip = this.originalDevice.productCategory === Product.grip;

        // Hide borderDrawStartOffsets for grip products since they use circular placement
        const borderDrawStartOffsetsGroup = document.getElementById("borderDrawStartOffsets-group");
        if (borderDrawStartOffsetsGroup) {
            borderDrawStartOffsetsGroup.style.display = isGrip ? "none" : "";
        }
    }

    getOriginalDevice() {
        return this.originalDevice;
    }

    getParameterOverrides() {
        return this.parameterOverrides;
    }
}

export const optionPanels = new OptionPanels();